// Metric-value PROVENANCE registry (measurement-contract §2, generalised from the
// calorie-only HC-8 registry). "Where does this number come from?" is the SAME
// question for every metric an app surface shows — a calorie figure, a heart-rate
// average, a step count — so the source registry that answers it must be
// metric-agnostic, not calorie-shaped. A source id (a Health-Connect dataOrigin
// package, or a synthetic id for a direct integration / JL's own compute) maps to
// ONE row here + one i18n key — never a hardcoded `if id === 'hc' → "Health
// Connect"` chain in a component, and never inline "estimated/measured" commentary
// on the number (Antenore 2026-07-10: a clean value + an (i) pill that reveals the
// origin by name).
//
// The `labelKey` is an i18n key, resolved by the display layer via `t()` — the
// registry holds NO human strings itself, so it stays locale-agnostic and a new
// source can't ship an English-only label.
//
// This module owns ONLY what is genuinely metric-agnostic: the source-ref shape
// and the origin→ref lookup (a device origin identifies its producer regardless of
// which metric the value carries). Metric-specific concepts — JL's calorie
// estimate, a calorie provenance ROW with its `kcal` field — stay in their own
// thin layer (see `energySources.ts`), built on top of this.

export type MetricSourceKind = "measured" | "estimate";

export interface MetricSourceRef {
  /** Stable id: the HC dataOrigin package for a measured value, or a synthetic
   *  id for a direct integration / an app's own computed value. */
  id: string;
  /** i18n key for the human-readable source name (never a literal string). */
  labelKey: string;
  kind: MetricSourceKind;
}

// Known measured-value origins, keyed by the Health-Connect `dataOrigin` package
// stored on `Session.externalOrigin`. Add a row to onboard a new producer — the
// (i) pill and any consumer pick it up with zero code change. A device origin
// names its producer independently of the metric (Zepp is Zepp whether the value
// is calories or heart rate), which is exactly why this registry generalises.
const MEASURED_ORIGINS: Record<string, MetricSourceRef> = {
  "com.huami.watch.hmwatchmanager": {
    id: "com.huami.watch.hmwatchmanager",
    labelKey: "mobile.energySource.zepp",
    kind: "measured",
  },
  "com.google.android.apps.fitness": {
    id: "com.google.android.apps.fitness",
    labelKey: "mobile.energySource.googleFit",
    kind: "measured",
  },
};

// A measured value with an unknown/absent origin still arrived via Health
// Connect — label it generically rather than leaking a raw package string.
const HEALTH_CONNECT_FALLBACK: MetricSourceRef = {
  id: "healthconnect",
  labelKey: "mobile.energySource.healthConnect",
  kind: "measured",
};

/** Resolve the source of a MEASURED value from its HC dataOrigin package. */
export const metricSourceForOrigin = (
  externalOrigin?: string,
): MetricSourceRef =>
  (externalOrigin ? MEASURED_ORIGINS[externalOrigin] : undefined) ??
  HEALTH_CONNECT_FALLBACK;
